import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Mail, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Button from "@components/common/Button";
import { SearchableSelect } from "@components/common/SearchableSelect";
import { TableViewControls } from "@components/common/TableViewControls";
import { TableViewLayout } from "@components/common/TableViewLayout";
import {
  useDeleteVendor,
  usePushVendorToErp,
  useVendorDuplicateCandidates,
  useVendorList,
} from "@api/hooks";
import {
  VendorsList,
  VendorDeleteModal,
  VendorDuplicateResolutionModal,
  VendorInvoiceRulesModal,
} from "@components/Vendors";
import { usePlanFeature } from "@api/hooks/useSubscription";
import { useGetErpConnections } from "@api/hooks/useErpConnection";
import { useInviteVendor, useVendorLinks } from "@api/hooks/useVendorPortal";
import {
  useStripeConnection,
  useRefreshVendorPaymentProfile,
  useStartVendorPaymentProfile,
  useVendorPaymentProfiles,
} from "@api/hooks/usePayments";
import { isTenantAdminUser, useAuthStore } from "@store/authStore";
import type {
  ERPConnectionDetailResponse,
  VendorDuplicateCandidate,
  VendorSearchResponse,
} from "@schemas/api";
import type { VendorPaymentProfile } from "@/types/payments";

const erpConnectionLabel = (connection: ERPConnectionDetailResponse) =>
  connection.display_name ||
  connection.external_company_name ||
  `${connection.erp_type} ${connection.realm_id}`;

export const VendorRecords: React.FC<{ hideHeader?: boolean }> = ({ hideHeader = false }) => {
  const navigate = useNavigate();
  const [skip, setSkip] = useState(0);
  const [deletingVendor, setDeletingVendor] =
    useState<VendorSearchResponse | null>(null);
  const [invitingVendor, setInvitingVendor] =
    useState<VendorSearchResponse | null>(null);
  const [paymentsVendor, setPaymentsVendor] =
    useState<VendorSearchResponse | null>(null);
  const [invoiceRulesVendor, setInvoiceRulesVendor] =
    useState<VendorSearchResponse | null>(null);
  const [erpPushVendor, setErpPushVendor] =
    useState<VendorSearchResponse | null>(null);
  const [duplicateCandidate, setDuplicateCandidate] =
    useState<VendorDuplicateCandidate | null>(null);
  const [erpPushConnectionId, setErpPushConnectionId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [erpConnectionFilter, setErpConnectionFilter] = useState("");
  const [vendorStatusFilter, setVendorStatusFilter] = useState("");
  const [portalFilter, setPortalFilter] = useState<"all" | "connected" | "not_connected">("all");
  const [paymentFilter, setPaymentFilter] = useState<"all" | "ready" | "needs_setup">("all");
  const [sortBy, setSortBy] = useState<"legal_name" | "status" | "payment_terms" | "erp_link" | "portal" | "payment">("legal_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const activeSearchTerm = deferredSearchTerm.trim();
  const emptyMessage = activeSearchTerm
    ? `No vendors found for "${activeSearchTerm}".`
    : erpConnectionFilter
      ? "No vendors found for this ERP link."
      : "No vendors found. Create one to get started.";
  const currentUser = useAuthStore((state) => state.currentUser);
  const tenantAdmin = isTenantAdminUser(currentUser);

  useEffect(() => {
    setSkip(0);
  }, [activeSearchTerm, erpConnectionFilter, paymentFilter, portalFilter, vendorStatusFilter]);

  const { data: vendorList, isLoading: isLoadingList } = useVendorList(
    skip,
    20,
    activeSearchTerm,
    erpConnectionFilter,
  );
  const { data: duplicateCandidates } = useVendorDuplicateCandidates(
    "needs_review",
    tenantAdmin,
  );
  const { data: vendorLinks } = useVendorLinks(0, 1000);
  const { data: stripeConnection } = useStripeConnection();
  const { data: paymentProfiles = [] } = useVendorPaymentProfiles();
  const deleteVendor = useDeleteVendor();
  const pushVendorToErp = usePushVendorToErp();
  const inviteVendor = useInviteVendor();
  const startVendorPaymentProfile = useStartVendorPaymentProfile();
  const refreshVendorPaymentProfile = useRefreshVendorPaymentProfile();
  const canManageVendorInvoiceRules = usePlanFeature("vendor_invoice_rules");
  const { data: erpConnections = [] } = useGetErpConnections();
  const activeErpConnections = useMemo(
    () => erpConnections.filter((connection) => connection.status === "active"),
    [erpConnections],
  );

  useEffect(() => {
    if (!erpPushVendor) return;
    setErpPushConnectionId("");
  }, [erpPushVendor]);

  const handleDelete = async () => {
    if (!deletingVendor) return;
    await deleteVendor.mutateAsync(deletingVendor.id);
    setDeletingVendor(null);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitingVendor || !inviteEmail) return;
    await inviteVendor.mutateAsync({
      vendor_record_id: invitingVendor.id,
      email: inviteEmail,
    });
    setInvitingVendor(null);
    setInviteEmail("");
  };

  const isProcessing = deleteVendor.isPending || pushVendorToErp.isPending;
  const vendorPortalStatusByVendorId = (vendorLinks?.items || []).reduce<
    Record<string, string>
  >((acc, link) => {
    acc[link.vendor_record_id] = link.status;
    return acc;
  }, {});
  const paymentProfilesByVendorId = paymentProfiles.reduce<
    Record<string, VendorPaymentProfile>
  >((acc, profile) => {
    acc[profile.vendor_id] = profile;
    return acc;
  }, {});
  const visibleVendors = useMemo(() => {
    const portalState = (vendor: VendorSearchResponse) => {
      const status = vendorPortalStatusByVendorId[vendor.id];
      return status === "registered" || status === "active" ? "connected" : "not_connected";
    };
    const paymentState = (vendor: VendorSearchResponse) =>
      paymentProfilesByVendorId[vendor.id]?.status === "ready" ? "ready" : "needs_setup";
    const valueFor = (vendor: VendorSearchResponse) => {
      if (sortBy === "payment_terms") return vendor.default_payment_terms ?? "";
      if (sortBy === "erp_link") return vendor.erp_links?.filter((link) => link.status === "active").map((link) => link.connection_label ?? link.erp_type).join(" ") ?? "";
      if (sortBy === "portal") return portalState(vendor);
      if (sortBy === "payment") return paymentState(vendor);
      return vendor[sortBy] ?? "";
    };

    return [...(vendorList?.items ?? [])]
      .filter((vendor) => !vendorStatusFilter || vendor.status === vendorStatusFilter)
      .filter((vendor) => portalFilter === "all" || portalState(vendor) === portalFilter)
      .filter((vendor) => paymentFilter === "all" || paymentState(vendor) === paymentFilter)
      .sort((left, right) => {
        const comparison = String(valueFor(left)).localeCompare(String(valueFor(right)));
        return sortOrder === "asc" ? comparison : -comparison;
      });
  }, [paymentFilter, paymentProfilesByVendorId, portalFilter, sortBy, sortOrder, vendorList?.items, vendorPortalStatusByVendorId, vendorStatusFilter]);
  const paymentsVendorProfile = paymentsVendor
    ? paymentProfilesByVendorId[paymentsVendor.id]
    : undefined;
  const isPendingVendorAction =
    paymentsVendorProfile?.status === "pending_vendor_action";
  const isVendorPaymentsReady = paymentsVendorProfile?.status === "ready";
  const canStartVendorStripeSetup =
    !isPendingVendorAction && !isVendorPaymentsReady;

  const stripeReady = stripeConnection?.status === "ready";

  const handleStartVendorPayments = async () => {
    if (!paymentsVendor) return;
    if (!canStartVendorStripeSetup) return;
    if (!stripeReady) return;

    await startVendorPaymentProfile.mutateAsync({
      vendorId: paymentsVendor.id,
      payload: {
        return_path: "/records?type=vendor",
        email: paymentsVendor.email || undefined,
        currency: paymentsVendor.currency_preference || undefined,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className={`flex items-center ${hideHeader ? "justify-end" : "justify-between"}`}>
        {!hideHeader && <h2 className="text-xl font-semibold text-content-primary">Vendors</h2>}
        <Button
          variant="primary"
          onClick={() => navigate("/records/vendors/new")}
          disabled={isProcessing}
        >
          <span className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Vendor
          </span>
        </Button>
      </div>

      {tenantAdmin && duplicateCandidates && duplicateCandidates.total > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-amber-900">
                <AlertTriangle className="h-5 w-5" />
                Vendor duplicates need review
              </h2>
              <p className="mt-1 text-sm text-amber-800">
                {duplicateCandidates.total} possible duplicate{" "}
                {duplicateCandidates.total === 1 ? "group" : "groups"} from ERP
                sync.
              </p>
            </div>
            <div className="grid gap-2">
              {duplicateCandidates.items.slice(0, 3).map((candidate) => (
                <div
                  key={candidate.id}
                  className="flex items-center justify-end gap-2"
                >
                  <span className="max-w-[360px] truncate text-sm text-amber-800">
                    {candidate.vendors
                      .map((vendor) => vendor.legal_name)
                      .join(" / ")}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setDuplicateCandidate(candidate)}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <TableViewLayout
        title="Vendors"
        summary={(() => {
          const total = vendorStatusFilter || portalFilter !== "all" || paymentFilter !== "all" ? visibleVendors.length : vendorList?.total ?? 0;
          return total ? `Showing ${skip + 1}-${Math.min(skip + 20, total)} of ${total}` : "Showing 0-0 of 0";
        })()}
      >
      <div className="border-b border-border-subtle px-5 py-3 dark:border-border-subtle-dark">
      <TableViewControls
        activeFilterCount={[
          erpConnectionFilter,
          vendorStatusFilter,
          portalFilter !== "all",
          paymentFilter !== "all",
        ].filter(Boolean).length}
        onClearFilters={() => {
          setErpConnectionFilter("");
          setVendorStatusFilter("");
          setPortalFilter("all");
          setPaymentFilter("all");
        }}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchLabel="Search vendors"
        searchPlaceholder="Search vendor names…"
        filterContent={
          <div className="space-y-5">
            <div>
              <label htmlFor="vendor-erp-link-filter" className="mb-2 block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">
                ERP Link
              </label>
              <SearchableSelect
                id="vendor-erp-link-filter"
                options={[
                  { id: "", label: "All ERP Links" },
                  ...activeErpConnections.map((connection) => ({ id: connection.id, label: erpConnectionLabel(connection) })),
                ]}
                value={erpConnectionFilter}
                onChange={setErpConnectionFilter}
                placeholder="All ERP Links"
                clearable={false}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">Vendor status</label>
              <SearchableSelect
                aria-label="Vendor status"
                options={[
                  { id: "", label: "All vendor statuses" },
                  ...Array.from(new Set((vendorList?.items ?? []).map((vendor) => vendor.status).filter(Boolean))).map((status) => ({ id: status!, label: status!.replace(/_/g, " ") })),
                ]}
                value={vendorStatusFilter}
                onChange={setVendorStatusFilter}
                clearable={false}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">Vendor portal</label>
              <SearchableSelect
                aria-label="Vendor portal status"
                options={[{ id: "all", label: "All portal states" }, { id: "connected", label: "Connected" }, { id: "not_connected", label: "Not connected" }]}
                value={portalFilter}
                onChange={(value) => setPortalFilter(value as typeof portalFilter)}
                clearable={false}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">Payment setup</label>
              <SearchableSelect
                aria-label="Vendor payment setup"
                options={[{ id: "all", label: "All payment states" }, { id: "ready", label: "Ready" }, { id: "needs_setup", label: "Needs setup" }]}
                value={paymentFilter}
                onChange={(value) => setPaymentFilter(value as typeof paymentFilter)}
                clearable={false}
              />
            </div>
          </div>
        }
        sortContent={
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">Sort by</label>
              <SearchableSelect
                aria-label="Sort vendors by"
                options={[
                  { id: "legal_name", label: "Vendor name" },
                  { id: "status", label: "Status" },
                  { id: "payment_terms", label: "Payment terms" },
                  { id: "erp_link", label: "ERP link" },
                  { id: "portal", label: "Vendor portal" },
                  { id: "payment", label: "Payment setup" },
                ]}
                value={sortBy}
                onChange={(value) => setSortBy(value as typeof sortBy)}
                clearable={false}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">Direction</label>
              <SearchableSelect
                aria-label="Vendor sort direction"
                options={[{ id: "asc", label: "Ascending" }, { id: "desc", label: "Descending" }]}
                value={sortOrder}
                onChange={(value) => setSortOrder(value as typeof sortOrder)}
                clearable={false}
              />
            </div>
          </div>
        }
      />
      </div>

      {vendorList && (
        <VendorsList
          vendors={visibleVendors}
          total={vendorStatusFilter || portalFilter !== "all" || paymentFilter !== "all" ? visibleVendors.length : vendorList.total}
          skip={skip}
          limit={20}
          onEdit={(vendor) => navigate(`/records/vendors/${vendor.id}/edit`)}
          onDelete={setDeletingVendor}
          onPageChange={setSkip}
          isLoading={isProcessing || isLoadingList}
          onInvite={setInvitingVendor}
          onSetupPayments={stripeReady ? setPaymentsVendor : undefined}
          onPushToErp={setErpPushVendor}
          onManageInvoiceRules={
            canManageVendorInvoiceRules ? setInvoiceRulesVendor : undefined
          }
          vendorPortalStatusByVendorId={vendorPortalStatusByVendorId}
          paymentProfilesByVendorId={paymentProfilesByVendorId}
          emptyMessage={emptyMessage}
        />
      )}
      </TableViewLayout>

      <VendorDeleteModal
        vendor={deletingVendor}
        isLoading={deleteVendor.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeletingVendor(null)}
      />

      {erpPushVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-900">
            <h2 className="text-lg font-semibold text-content-primary dark:text-content-primary-dark">
              Connect vendor to ERP
            </h2>
            <p className="mt-1 text-sm text-content-secondary">
              Choose where {erpPushVendor.legal_name} should be created.
            </p>
            <label className="mt-4 block text-sm font-medium text-content-secondary">
              ERP company
            </label>
            <SearchableSelect
              options={[
                { id: "", label: "Choose ERP company" },
                ...activeErpConnections.map((connection) => ({
                  id: connection.id,
                  label: erpConnectionLabel(connection),
                })),
              ]}
              value={erpPushConnectionId}
              onChange={setErpPushConnectionId}
              placeholder="Choose ERP company"
              clearable={false}
              className="mt-1"
            />
            <div className="mt-5 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setErpPushVendor(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!erpPushConnectionId || pushVendorToErp.isPending}
                onClick={() =>
                  pushVendorToErp.mutate(
                    {
                      vendorId: erpPushVendor.id,
                      erpConnectionId: erpPushConnectionId,
                    },
                    { onSuccess: () => setErpPushVendor(null) },
                  )
                }
              >
                {pushVendorToErp.isPending ? "Connecting..." : "Create in ERP"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <VendorInvoiceRulesModal
        vendor={invoiceRulesVendor}
        onClose={() => setInvoiceRulesVendor(null)}
      />

      <VendorDuplicateResolutionModal
        candidate={duplicateCandidate}
        onClose={() => setDuplicateCandidate(null)}
      />

      {/* Invite Vendor Modal */}
      {invitingVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-surface-base-dark rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Invite Vendor
              </h2>
              <button
                onClick={() => {
                  setInvitingVendor(null);
                  setInviteEmail("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Send an invitation to{" "}
              <span className="font-medium">{invitingVendor.legal_name}</span>{" "}
              to join EZ Flow as a vendor.
            </p>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vendor Email
                </label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={invitingVendor.email || "vendor@company.com"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setInvitingVendor(null);
                    setInviteEmail("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={inviteVendor.isPending}
                >
                  <span className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {inviteVendor.isPending ? "Sending..." : "Send Invitation"}
                  </span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {paymentsVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-surface-base-dark rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Stripe Payout Setup
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Start vendor onboarding and refresh payout readiness for{" "}
                  {paymentsVendor.legal_name}.
                </p>
              </div>
              <button
                onClick={() => setPaymentsVendor(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium">
                Tenant Stripe:{" "}
                {(stripeConnection?.status || "not_started").replace(/_/g, " ")}
              </p>
              <p className="font-medium">
                Current status:{" "}
                {(paymentsVendorProfile?.status || "not_started").replace(
                  /_/g,
                  " ",
                )}
              </p>
              <p className="mt-1">
                {!stripeReady
                  ? "Connect the tenant Stripe account from Company Profile before linking vendor payouts."
                  : isVendorPaymentsReady
                    ? "This vendor is already ready for Stripe payouts."
                    : isPendingVendorAction
                      ? "The vendor already has an active Stripe onboarding action. Refresh after they complete it."
                      : "Start or link the vendor Stripe payout account for this tenant."}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  refreshVendorPaymentProfile.mutateAsync(paymentsVendor.id)
                }
                disabled={refreshVendorPaymentProfile.isPending}
              >
                Refresh Status
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleStartVendorPayments}
                disabled={
                  startVendorPaymentProfile.isPending ||
                  !canStartVendorStripeSetup ||
                  !stripeReady
                }
              >
                {startVendorPaymentProfile.isPending
                  ? "Starting…"
                  : !stripeReady
                    ? "Tenant Stripe Not Connected"
                    : isVendorPaymentsReady
                      ? "Vendor Already Connected"
                      : isPendingVendorAction
                        ? "Waiting on Vendor"
                        : "Start Stripe Setup"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
